import { useCallback, useEffect, useRef, useState } from 'react'
import { Building2, ChevronRight, Plus, Search, Users, X } from 'lucide-react'
import { useHistory } from 'react-router-dom'

import { useCenterStore } from '@caradasstore/CenterStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { FormModal } from '@components/branded-modal'
import { SearchOrAddData, ItemPerPageSelector } from '@components/common'
import { useSessionState } from '@hooks/useSessionState'
import { AdasBox, AdasPagination, AdasTable } from '@components/wrapper-components'
import { CLIENT_LIST_HEADERS, CLIENT_TYPE_ENUM, LABEL_DISPLAY, getClientDetailsUrl } from 'constants'
import { getClients, uploadClientCSV, validateClientCSV } from '../../api/api'
import { GENERIC_MESSAGES } from 'constants'
import { downloadAsFile } from 'utils/utils'
import { Logger } from '../../logger'
import { ClientForm } from '@components/client'
import { useUserStore } from '@caradasstore/UserStore'
import { ImportCSVComponent } from '@components/import-form/ImportCSVComponent'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { isClientPausedByPlan, limitByKey, useLimitStore } from '@caradasstore/LimitStore'
import { tierById } from '../../data/catalog'
import { useCatalog } from '../../store/CatalogStore'

const getClientInitials = (name) => {
  if (!name) return ''
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const IMPORT_CLIENT_STEPPER = [0, 1]

export const ClientListPage = () => {
  const [searchInput, setSearchInput] = useSessionState('clients:searchInput', '')
  const [fullClientList, setFullClientList] = useState([])
  const [existingClientNames, setExistingClientNames] = useState([])
  const [clients, setClients] = useState([])
  const [isAddClient, setIsAddClient] = useState(false)
  const isReadOnly = useIsReadOnly()
  // The plan's client limit (0173): how many clients the location may have, which ones it keeps enabled when
  // over the limit (after a downgrade), and the plan that lifts it.
  const limitState = useLimitStore()
  const loadLimits = useLimitStore((s) => s.loadLimits)
  const { catalog } = useCatalog()
  const clientLimit = limitByKey(limitState, 'clients_per_location')
  const clientLimitReached = Boolean(clientLimit && clientLimit.limit != null && clientLimit.remaining === 0)
  const clientUpgradeName = tierById(catalog, clientLimit?.upgradeTier)?.name
  // Sorting was removed from the Clients table; the list is always shown A–Z by name.
  const sort = 'ASC'
  const [openImportForm, setOpenImportForm] = useState(false)
  const [activeStep, setActiveStep] = useState(IMPORT_CLIENT_STEPPER[0])
  const [file, setFile] = useState(null)
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState(null)
  const [csvValid, setCsvValid] = useState(true)
  const [CSVValidationResponse, setCSVValidationResponse] = useState(null)
  const [CSVUploadResponse, setCSVUploadResponse] = useState(null)
  const [page, setPage] = useSessionState('clients:page', 1)
  const [rowsPerPage, setRowsPerPage] = useSessionState('clients:rowsPerPage', 50)
  const ccid = useCenterStore((store) => store.centerDetail?.id)
  const history = useHistory()
  const user = useUserStore((store) => store.currentUser)
  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  const searchInputHandler = (e) => {
    setSearchInput(e.target.value)
  }

  const fetchClient = useCallback(async () => {
    const clientListResponse = await getClients({
      ccid: ccid,
      isActiveOnly: false
    })
    if (clientListResponse?.status === 200) {
      setFullClientList(clientListResponse.data)
      setExistingClientNames(clientListResponse.data.map((x) => x.name))
    }
  }, [ccid])

  // Initial Load
  useEffect(() => {
    fetchClient()
  }, [fetchClient])

  useEffect(() => {
    if (ccid) loadLimits(ccid, { force: true })
  }, [ccid, loadLimits, fullClientList.length])

  const sortByName = useCallback((a, b, sort) => {
    const name1 = sort === 'ASC' ? a.name.toUpperCase() : b.name.toUpperCase()
    const name2 = sort === 'ASC' ? b.name.toUpperCase() : a.name.toUpperCase()

    let comparison = 0

    if (name1 > name2) {
      comparison = 1
    } else if (name1 < name2) {
      comparison = -1
    }
    return comparison
  }, [])

  useEffect(() => {
    let list = [...fullClientList]
    if (searchInput) {
      list = list.filter((client) => client.name.toLowerCase().includes(searchInput))
    }
    list = list.map((client) => {
      const typeDescription = CLIENT_TYPE_ENUM[client.type] || ''
      return { ...client, typeDescription }
    })
    list.sort((a, b) => sortByName(a, b, sort))

    setClients(list)
  }, [sort, fullClientList, sortByName, searchInput])

  // Reset to page 1 when the user changes filters mid-session, but skip the very
  // first run so the persisted page from a prior visit isn't wiped on remount.
  const didInitClientFiltersRef = useRef(false)
  useEffect(() => {
    if (!didInitClientFiltersRef.current) {
      didInitClientFiltersRef.current = true
      return
    }
    setPage(1)
  }, [searchInput, sort, setPage])

  const totalPages = Math.ceil(clients.length / rowsPerPage)
  const startIndex = (page - 1) * rowsPerPage
  const endIndex = Math.min(startIndex + rowsPerPage, clients.length)
  const paginatedClients = clients.slice(startIndex, endIndex)

  const clientInfoHandler = (props) => {
    history.push(getClientDetailsUrl(props?.id))
  }

  const handlePageChange = (event, value) => {
    setPage(value)
  }

  const handlePageSizeChange = (event) => {
    setRowsPerPage(Number(event.target.value))
    setPage(1)
  }

  const downloadClients = useCallback(
    ({ template = false }) => {
      // Escape every field so commas, quotes, and newlines in the data can't shift columns.
      const csvCell = (val) => {
        const s = (val === null || val === undefined ? '' : String(val)).replace(/\r?\n/g, ' ')
        return '"' + s.replace(/"/g, '""') + '"'
      }
      const toRow = (cells) => cells.map(csvCell).join(',') + '\n'

      const header = toRow([
        'Name',
        'CCC ID',
        'Type',
        'Potential Monthly RO Count',
        'Rebate/Discount Percent',
        'Status',
        'Phone',
        'Email',
        'Address',
        'City',
        'State',
        'Zip'
      ])

      const rows = template
        ? []
        : fullClientList.map((client) =>
            toRow([
              client.name,
              client.ccc_client_id,
              client.type,
              client.vehicle_count ? client.vehicle_count : 0,
              client.rebate_percent,
              client.is_active,
              client.phone,
              client.email,
              [client.address1, client.address2].filter(Boolean).join(', '),
              client.city,
              client.state,
              client.zip
            ])
          )

      const fileName = 'clientExport.csv'
      const value = new Blob([header, ...rows], { type: 'text/plain' })
      downloadAsFile(value, fileName)
    },
    [fullClientList]
  )

  const handleValidateCSV = useCallback(async () => {
    if (file instanceof File) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      try {
        const response = await validateClientCSV({ file: formData, ccid })
        if (response.data.status === 'error') {
          setCsvValid(false)
        } else {
          setCsvValid(true)
        }
        setCSVValidationResponse(response.data.messages)
        setActiveStep((step) => step + 1)
      } catch (error) {
        if (error.status === 400) {
          setCSVValidationResponse(error.response.data.messages)
          setCsvValid(false)
          setActiveStep((step) => step + 1)
        } else {
          Logger.error({ message: error, payload: { file: 'ClientListPage', method: 'handleValidateCSV' } })
        }
      }
    }
  }, [file, validateClientCSV, setActiveStep, setCSVValidationResponse, setCsvValid, ccid])

  const handleCSVUpload = useCallback(async () => {
    if (file instanceof File) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      const response = await uploadClientCSV({ file: formData, ccid })
      setCSVUploadResponse(response.data.messages)
      setActiveStep((step) => step + 1)
    }
  }, [file, uploadClientCSV, setActiveStep, ccid])

  const handleCloseImportClient = useCallback(() => {
    setFile(null)
    setFileUploadErrorMessage(null)
    setActiveStep(IMPORT_CLIENT_STEPPER[0])
    setCSVValidationResponse(null)
    setOpenImportForm(false)
    setCsvValid(true)
    fetchClient()
  }, [])

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 pb-[10px] min-[435px]:px-0'>
      {/* Page heading (desktop only — mobile uses the global toolbar title) */}
      <AdasBox className='hidden min-[435px]:flex pt-6 md:pt-4 mb-6 flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>Clients</h1>
      </AdasBox>

      {/* Mobile inline search bar — triggered by the toolbar search icon */}
      {searchOpen && (
        <AdasBox className='min-[435px]:hidden mb-3 mt-2 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            placeholder='Search clients'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </AdasBox>
      )}

      {clientLimit && clientLimit.limit != null && (
        <AdasBox
          data-testid='client-plan-limit'
          className={`mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-[13px] ${
            clientLimitReached ? 'border-[#FDE68A] bg-[#FFFBEB] text-[#92400E]' : 'border-border bg-white text-foreground'
          }`}
        >
          <span>
            <span className='font-semibold'>
              {clientLimit.used} of {clientLimit.limit} clients
            </span>{' '}
            on the {limitState.tierName || 'current'} plan
            {clientLimitReached ? ' · limit reached' : ''}
            {Array.isArray(limitState.enabledClientIds) && clientLimit.used > clientLimit.limit && (
              <>
                {' '}
                · {clientLimit.used - clientLimit.limit} paused by the plan:{' '}
                {fullClientList
                  .filter((c) => c.is_active && isClientPausedByPlan(limitState, c.id))
                  .map((c) => c.name)
                  .join(', ')}
              </>
            )}
          </span>
          {clientUpgradeName && (clientLimitReached || clientLimit.used > clientLimit.limit) && (
            <button
              type='button'
              onClick={() => history.push('/settings?section=billing')}
              className='font-semibold text-[#1F6FEB] hover:underline'
            >
              Upgrade to {clientUpgradeName}
            </button>
          )}
        </AdasBox>
      )}

      {/* Search bar + Action buttons (desktop only) */}
      <AdasBox className='hidden min-[435px]:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-5'>
        <AdasBox className='flex items-center flex-1 sm:flex-[0.6]'>
          <SearchOrAddData
            searchInput={searchInput}
            maxHeight={'40px'}
            searchInputHandler={searchInputHandler}
            onAddClick={() => setIsAddClient(true)}
            variant={clientLimitReached ? 'withAddButtonDisabled' : undefined}
          />
        </AdasBox>
        <AdasBox className='flex items-center gap-3 shrink-0'>
          {!isReadOnly && (
            <button
              onClick={() => setOpenImportForm(true)}
              className='inline-flex items-center gap-1.5 h-10 px-5 bg-sidebar text-white text-sm font-medium rounded-lg hover:bg-sidebar/90 transition-colors'
            >
              Import CSV
            </button>
          )}
          {/* Export is a read, so it stays. */}
          <button
            onClick={downloadClients}
            className='h-10 px-4 border border-border rounded-lg text-[13px] font-medium text-foreground hover:bg-muted transition-colors'
          >
            Export CSV
          </button>
        </AdasBox>
      </AdasBox>
      {isAddClient && (
        <FormModal open={isAddClient} onClose={() => setIsAddClient(false)} title='Client Info' icon={Building2} width='max-w-[520px]'>
          <ClientForm
            onSaveSuccess={fetchClient}
            clibrationCenterId={ccid}
            close={() => setIsAddClient(false)}
            existingClients={existingClientNames}
          />
        </FormModal>
      )}
      {/* Table Card (desktop only) */}
      <div className='hidden min-[435px]:block bg-card rounded-card border border-border shadow-level-1 overflow-hidden mt-5'>
        {paginatedClients.length > 0 && (
          <>
            <AdasTable
              containerProps={{ className: 'rounded-none border-none shadow-none' }}
              columns={CLIENT_LIST_HEADERS}
              gradientHeader
              sortField=''
              sortDirection={sort}
              onSort={() => {}}
              data={paginatedClients}
              onSelect={clientInfoHandler}
            />
            {/* Pagination */}
            <div className='flex items-center justify-between px-5 py-3 border-t border-border'>
              <span className='text-[13px] text-muted-foreground min-w-[150px]'>
                Showing {startIndex + 1}-{endIndex} of {clients.length}
              </span>
              <AdasPagination page={page} count={totalPages} onChange={handlePageChange} />
              <div className='w-[20%]'>
                <ItemPerPageSelector itemPerPageList={[25, 50, 100]} pageSizeHandler={handlePageSizeChange} pageSize={rowsPerPage} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile card list */}
      <AdasBox className='min-[435px]:hidden space-y-3 pb-24'>
        {paginatedClients.length === 0 ? (
          <AdasBox className='rounded-xl border border-[#D1D5D8] bg-white px-4 py-10 text-center'>
            <p className='text-[14px] text-[#6B7280]'>
              {searchInput ? 'No clients match your search.' : 'No clients yet. Tap Add Client to get started.'}
            </p>
          </AdasBox>
        ) : (
          paginatedClients.map((client) => {
            const rebateLabel = client.rebate_percent !== null && client.rebate_percent !== undefined ? `${client.rebate_percent}%` : '—'
            const roCount = client.vehicle_count ?? '—'
            return (
              <button
                key={client.id}
                type='button'
                onClick={() => clientInfoHandler(client)}
                className='w-full rounded-xl border border-[#D1D5D8] bg-white p-4 text-left transition-colors hover:bg-[#F8FAFC]'
              >
                <div className='flex items-start justify-between gap-3'>
                  <div className='flex min-w-0 flex-1 items-start gap-3'>
                    <div className='flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-[#E5EBF5] text-[14px] font-bold text-[#0A1628]'>
                      {getClientInitials(client.name)}
                    </div>
                    <div className='min-w-0 flex-1'>
                      <div className='truncate text-[16px] font-bold leading-tight text-[#0A1628]'>{client.name}</div>
                      <div className='mt-1 inline-flex items-center'>
                        {client.is_active && isClientPausedByPlan(limitState, client.id) ? (
                          <span className='inline-flex items-center justify-center rounded-full bg-[#FEF3C7] px-2.5 py-0.5 text-[11px] font-semibold text-[#B45309]'>
                            Paused by plan
                          </span>
                        ) : client.is_active ? (
                          <span className='inline-flex items-center justify-center rounded-full bg-[#DCFCE7] px-2.5 py-0.5 text-[11px] font-semibold text-[#16803F]'>
                            Active
                          </span>
                        ) : (
                          <span className='inline-flex items-center justify-center rounded-full bg-[#EBEDF0] px-2.5 py-0.5 text-[11px] font-semibold text-[#6B7280]'>
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className='mt-1 h-5 w-5 flex-shrink-0 text-[#9CA3AF]' />
                </div>
                <div className='mt-3 grid grid-cols-3 gap-3 border-t border-[#EBEDF0] pt-3 text-[12px]'>
                  <div>
                    <div className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>
                      {LABEL_DISPLAY.MONTHLY_VEHICLECOUNT_LABEL}
                    </div>
                    <div className='mt-0.5 text-[13px] font-semibold text-[#0A1628]'>{roCount}</div>
                  </div>
                  <div>
                    <div className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>Rebate %</div>
                    <div className='mt-0.5 text-[13px] font-semibold text-[#0A1628]'>{rebateLabel}</div>
                  </div>
                  <div>
                    <div className='text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]'>Auto-Apply</div>
                    <div className={`mt-0.5 text-[13px] font-semibold ${client.auto_apply_rebate ? 'text-[#16803F]' : 'text-[#6B7280]'}`}>
                      {client.auto_apply_rebate ? 'Enabled' : 'Disabled'}
                    </div>
                  </div>
                </div>
              </button>
            )
          })
        )}

        {/* Mobile pagination */}
        {paginatedClients.length > 0 && totalPages > 1 && (
          <AdasBox className='flex justify-center pt-2'>
            <AdasPagination page={page} count={totalPages} onChange={handlePageChange} />
          </AdasBox>
        )}
      </AdasBox>

      {/* Floating Add Client button (mobile only) */}
      {!isReadOnly && (
        <button
          type='button'
          onClick={() => setIsAddClient(true)}
          disabled={clientLimitReached}
          title={clientLimitReached ? 'Your plan’s client limit is reached.' : undefined}
          className='min-[435px]:hidden fixed bottom-6 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-[#0A1628] px-6 py-3.5 text-[14px] font-bold text-white shadow-lg transition-colors hover:bg-[#1F2D3D] disabled:opacity-60'
        >
          <Plus className='h-5 w-5' />
          Add Client
        </button>
      )}
      <ImportCSVComponent
        open={openImportForm}
        title='Bulk Import Clients'
        subtitle='Add multiple clients at once from a CSV file'
        icon={Users}
        templateFileName='clients_template.csv'
        uploadVariant='row'
        onClose={handleCloseImportClient}
        handleValidateCSV={handleValidateCSV}
        handleCSVUpload={handleCSVUpload}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        csvValid={csvValid}
        downloadCSV={downloadClients}
        CSVValidationResponse={CSVValidationResponse}
        file={file}
        IMPORT_CLIENT_STEPPER={IMPORT_CLIENT_STEPPER}
        GENERIC_MESSAGES={GENERIC_MESSAGES}
        CSVUploadResponse={CSVUploadResponse}
        setCsvValid={setCsvValid}
        setFile={setFile}
        fileUploadErrorMessage={fileUploadErrorMessage}
        setFileUploadErrorMessage={setFileUploadErrorMessage}
      />
    </div>
  )
}
